/**
 * Commitment state service — assembles the inputs the pure judgment needs.
 *
 * PRD: docs/9-exploration/DISPLACEMENT-AND-COMMITMENT-STATES-PRD.md (D1).
 * Judgment: src/lib/commitmentState.ts (pure; all the decisions live there).
 *
 * Three reads, no new math:
 *   1. claims  — plan_resource_claims for the cycle, via resourceClaimsService.
 *   2. items   — items.work_type for the commitments in scope.
 *   3. recipes — execution_recipe_templates for the workspace, whose demand
 *                template + uncertainty multiplier become the requirement via
 *                computeRecipeResourceDemand() (the existing, previously
 *                caller-less function built for exactly this).
 *
 * The uncertainty multiplier is applied HERE, once, by
 * computeRecipeResourceDemand's default. The planning engine applies it on its
 * own separate path; these two numbers are never combined, so nothing is
 * double-multiplied.
 */

import { getSupabaseClient } from './supabaseClient';
import { fetchHeldValues } from './itemCycleStandingService';
import { listClaimsForCycle, type PlanResourceClaimRow } from './resourceClaimsService';
import { listExecutionRecipes } from './executionRecipeService';
import { computeRecipeResourceDemand, type ExecutionRecipeTemplate } from '../types/work-type-recipes';
import {
  deriveCommitmentState,
  rollUpCommitmentStates,
  type CommitmentClaim,
  type CommitmentStateRollup,
  type CommitmentStateView,
  type ResourceRequirementLine,
} from './commitmentState';

export interface CommitmentStatesForCycle {
  /** Keyed on items.id — the durable commitment identity claims use. */
  byItemId: Map<string, CommitmentStateView>;
  rollup: CommitmentStateRollup;
  /**
   * True when no recipe in the workspace carries a demand template, so no
   * requirement is computable for anyone. Surfaces say this once at the top
   * rather than repeating "no resource basis" on every row (D0-2).
   */
  noDemandTemplates: boolean;
  /**
   * Ids that were asked about but are not items. The legacy
   * `runEngine(projectId, …)` path ranks `planning_priorities.id` values,
   * which are NOT item ids and do not join to plan_resource_claims.item_id.
   * Those ids get NO entry in `byItemId` — an unidentifiable commitment gets
   * no verdict rather than a confident "announced".
   */
  unidentifiedIds: readonly string[];
  error?: string;
}

export const EMPTY_COMMITMENT_STATES: CommitmentStatesForCycle = {
  byItemId: new Map(),
  rollup: rollUpCommitmentStates([]),
  noDemandTemplates: true,
  unidentifiedIds: [],
};

export interface LoadCommitmentStatesInput {
  planCycleId: string;
  /** The commitments in scope — items.id values. */
  itemIds: readonly string[];
  workspaceId: string | null | undefined;
  /** Scenario.defaultWorkUnitHours — work units become hours through it. */
  defaultWorkUnitHours: number;
  /** Coverage threshold for `committed`; defaults to 100% (PRD §4). */
  threshold?: number;
}

/**
 * Picks the recipe that governs a work type, mirroring
 * `resolveRecipeIdForWorkType`'s precedence: an active workspace-custom row
 * wins over the global built-in for the same ref. Exported for tests — the
 * precedence rule is the kind of thing that silently rots.
 */
export function resolveRecipeForWorkType(
  recipes: readonly ExecutionRecipeTemplate[],
  workType: string
): ExecutionRecipeTemplate | null {
  const candidates = recipes.filter((r) => r.work_type_ref === workType && r.active);
  if (candidates.length === 0) return null;
  return candidates.find((r) => r.workspace_id != null) ?? candidates[0];
}

/**
 * The recipe-derived requirement for one item. Empty when the item has no
 * work type, its work type has no recipe (D0-3), or the recipe has no demand
 * template (D0-2) — all three read the same way downstream: no basis, never
 * "requires nothing".
 */
export function requirementForRecipe(
  recipe: ExecutionRecipeTemplate | null,
  defaultWorkUnitHours: number
): ResourceRequirementLine[] {
  if (!recipe) return [];
  const demand = computeRecipeResourceDemand(recipe, defaultWorkUnitHours);
  return Object.entries(demand.byResourceHours)
    .filter(([, hours]) => hours > 0)
    .map(([resourceType, hours]) => ({ resourceType, hours }));
}

/** Narrows a stored claim row to the fields the judgment reads. */
export function toCommitmentClaim(row: PlanResourceClaimRow): CommitmentClaim {
  return {
    claimKind: row.claim_kind as CommitmentClaim['claimKind'],
    status: row.status as CommitmentClaim['status'],
    resourceType: row.resource_type,
    declaredHours: row.declared_hours == null ? null : Number(row.declared_hours),
    declaredAmount: row.declared_amount == null ? null : Number(row.declared_amount),
    currency: row.currency,
  };
}

export async function loadCommitmentStatesForCycle(
  input: LoadCommitmentStatesInput
): Promise<CommitmentStatesForCycle> {
  const itemIds = [...new Set(input.itemIds)];
  if (itemIds.length === 0) return EMPTY_COMMITMENT_STATES;

  const [claimsResult, recipesResult, workTypes] = await Promise.all([
    listClaimsForCycle(input.planCycleId),
    listExecutionRecipes(input.workspaceId ?? null),
    fetchWorkTypes(itemIds),
  ]);

  const error = claimsResult.error ?? recipesResult.error ?? workTypes.error ?? undefined;

  const claimsByItem = new Map<string, CommitmentClaim[]>();
  for (const row of claimsResult.rows) {
    const list = claimsByItem.get(row.item_id) ?? [];
    list.push(toCommitmentClaim(row));
    claimsByItem.set(row.item_id, list);
  }

  const recipes = recipesResult.data;
  const noDemandTemplates = !recipes.some(
    (r) => (r.resource_allocations ?? []).some((a) => a?.resourceType && a.workUnits > 0)
  );

  const requirementCache = new Map<string, ResourceRequirementLine[]>();
  const byItemId = new Map<string, CommitmentStateView>();
  const unidentifiedIds: string[] = [];

  for (const itemId of itemIds) {
    if (!workTypes.byItemId.has(itemId)) {
      unidentifiedIds.push(itemId);
      continue;
    }
    const workType = workTypes.byItemId.get(itemId) ?? null;
    let requirements: ResourceRequirementLine[] = [];
    if (workType) {
      const cached = requirementCache.get(workType);
      if (cached) {
        requirements = cached;
      } else {
        requirements = requirementForRecipe(
          resolveRecipeForWorkType(recipes, workType),
          input.defaultWorkUnitHours
        );
        requirementCache.set(workType, requirements);
      }
    }
    byItemId.set(
      itemId,
      deriveCommitmentState({
        requirements,
        claims: claimsByItem.get(itemId) ?? [],
        threshold: input.threshold,
      })
    );
  }

  return {
    byItemId,
    rollup: rollUpCommitmentStates([...byItemId.values()]),
    noDemandTemplates,
    unidentifiedIds,
    error,
  };
}

/**
 * The commitments on a cycle's current record — distinct `initiatives.item_id`
 * on the cycle's latest plan version. This is the "12 ratified commitments"
 * denominator of the portfolio rollup, and the same (plan_cycle_id, item_id)
 * grain claims key on.
 */
export async function listCommitmentItemIdsForCycle(
  planCycleId: string
): Promise<{ itemIds: string[]; titleByItemId: Map<string, string>; error?: string }> {
  const titleByItemId = new Map<string, string>();
  const supabase = getSupabaseClient();
  if (!supabase) return { itemIds: [], titleByItemId, error: 'Supabase client unavailable' };

  const { data: versions, error: versionError } = await supabase
    .from('plan_versions')
    .select('id, version_number')
    .eq('plan_cycle_id', planCycleId)
    .order('version_number', { ascending: false })
    .limit(1);
  if (versionError) return { itemIds: [], titleByItemId, error: versionError.message };
  const versionId = versions?.[0]?.id as string | undefined;
  if (!versionId) return { itemIds: [], titleByItemId };

  const { data, error } = await supabase
    .from('initiatives')
    .select('item_id, title')
    .eq('plan_version_id', versionId);
  if (error) return { itemIds: [], titleByItemId, error: error.message };

  const ids = new Set<string>();
  for (const row of data ?? []) {
    const id = row.item_id as string | null;
    if (!id) continue;
    ids.add(id);
    const title = (row.title as string | null)?.trim();
    if (title && !titleByItemId.has(id)) titleByItemId.set(id, title);
  }
  return { itemIds: [...ids], titleByItemId };
}

export async function fetchWorkTypes(
  itemIds: readonly string[]
): Promise<{ byItemId: Map<string, string | null>; error?: string }> {
  const byItemId = new Map<string, string | null>();
  const supabase = getSupabaseClient();
  if (!supabase) return { byItemId, error: 'Supabase client unavailable' };

  const { data, error } = await supabase
    .from('items')
    .select('id, work_type')
    .in('id', itemIds as string[]);
  if (error) return { byItemId, error: error.message };
  for (const row of data ?? []) {
    byItemId.set(row.id as string, (row.work_type as string | null) ?? null);
  }
  // A plan in force keeps the work type it authorized until it is amended (migration 1019).
  const held = await fetchHeldValues(itemIds);
  if (!held) return { byItemId, error: 'Could not read what the plan holds' };
  for (const [itemId, values] of held) byItemId.set(itemId, values.workType);
  return { byItemId };
}
